import * as Sentry from "@sentry/vue";

interface Props {
  message: string;
  extra?: Record<string, unknown>;
  level: Sentry.SeverityLevel;
}

const stringifyExtraValues = (extra: Record<string, unknown>): Record<string, string> => {
  const stringifiedEntries = Object.entries(extra).map(([key, value]) => {
    try {
      return [key, JSON.stringify(value, null, 2)];
    } catch {
      return [key, "[Unserializable]"];
    }
  });

  return Object.fromEntries(stringifiedEntries);
};

/**
 * A universal logging function that logs messages to Sentry or the console in a local environment.
 *
 * @param {Object} props - An object containing logging details.
 * @param {Sentry.SeverityLevel} props.level - The log severity level (e.g., "info", "warning", "error").
 * @param {string} props.message - The message to be logged.
 * @param {Record<string, unknown>} [props.extra] - Additional data to send to Sentry.
 *
 * @example
 * logger({ level: "error", message: "API connection failed", extra: { endpoint: "/products", status: 500 } });
 *
 * @example
 * logger({ level: "info", message: "User logged in", extra: { userId: "12345" } });
 */
export const logger = ({ level, message, extra }: Props): void => {
  Sentry.withScope((scope) => {
    scope.setLevel(level);

    if (extra) {
      scope.setExtras(stringifyExtraValues(extra));
    }

    Sentry.captureMessage(message);
  });
};
